import { createStore } from 'vuex'

const store = createStore({
  state() {
    return {
      user: {
        name: localStorage.getItem('user_name') || '',
        startDate: localStorage.getItem('start_date') || null,
        id: localStorage.getItem('user_id') || null
      },
      progress: {
        completedDays: JSON.parse(localStorage.getItem('completed_days') || '[]'),
        completedCommands: JSON.parse(localStorage.getItem('completed_commands') || '[]'),
        notes: JSON.parse(localStorage.getItem('captain_log') || '{}'),
        taskStatus: JSON.parse(localStorage.getItem('task_status') || '{}')
      },
      challenges: [],
      currentDay: parseInt(localStorage.getItem('current_day') || '1', 10),
      apiKey: localStorage.getItem('apiKey') || '',
      isApiKeyValid: false,
      syncEnabled: false
    }
  },
  getters: {
    userName(state) {
      return state.user.name
    },
    startDate(state) {
      return state.user.startDate
    },
    completedDays(state) {
      return state.progress.completedDays
    },
    completedCommands(state) {
      return state.progress.completedCommands
    },
    captainsLog(state) {
      return state.progress.notes
    },
    currentChallenge(state) {
      return state.challenges.find(challenge => challenge.day === state.currentDay) || null
    },
    progressPercentage(state) {
      return Math.round((state.progress.completedDays.length / 30) * 100)
    },
    getTaskStatus: (state) => (day, taskId) => {
      return state.progress.taskStatus[`${day}-${taskId}`] || false
    },
    isSyncEnabled(state) {
      return state.syncEnabled && state.apiKey && state.isApiKeyValid
    }
  },
  mutations: {
    setUserName(state, name) {
      state.user.name = name
      localStorage.setItem('user_name', name)
    },
    setStartDate(state, date) {
      state.user.startDate = date
      localStorage.setItem('start_date', date)
    },
    setCurrentDay(state, day) {
      state.currentDay = day
      localStorage.setItem('current_day', day.toString())
    },
    setChallenges(state, challenges) {
      state.challenges = challenges
    },
    completeDay(state, day) {
      if (!state.progress.completedDays.includes(day)) {
        state.progress.completedDays.push(day)
        state.progress.completedDays.sort((a, b) => a - b)
        localStorage.setItem('completed_days', JSON.stringify(state.progress.completedDays))
      }
    },
    uncompleteDay(state, day) {
      state.progress.completedDays = state.progress.completedDays.filter(d => d !== day)
      localStorage.setItem('completed_days', JSON.stringify(state.progress.completedDays))
    },
    completeCommand(state, command) {
      if (!state.progress.completedCommands.includes(command)) {
        state.progress.completedCommands.push(command)
        localStorage.setItem('completed_commands', JSON.stringify(state.progress.completedCommands))
      }
    },
    addLogEntry(state, { day, note }) {
      state.progress.notes[day] = note
      localStorage.setItem('captain_log', JSON.stringify(state.progress.notes))
    },
    updateTaskStatus(state, { day, taskId, completed }) {
      const key = `${day}-${taskId}`
      state.progress.taskStatus[key] = completed
      localStorage.setItem('task_status', JSON.stringify(state.progress.taskStatus))
    },
    resetProgress(state) {
      state.progress.completedDays = []
      state.progress.completedCommands = []
      state.progress.notes = {}
      state.progress.taskStatus = {}
      localStorage.removeItem('completed_days')
      localStorage.removeItem('completed_commands')
      localStorage.removeItem('captain_log')
      localStorage.removeItem('task_status')
    },
    setApiKey(state, key) {
      state.apiKey = key
      localStorage.setItem('apiKey', key)
    },
    setApiKeyValid(state, isValid) {
      state.isApiKeyValid = isValid
    },
    setSyncEnabled(state, enabled) {
      state.syncEnabled = enabled
    },
    setUserId(state, userId) {
      state.user.id = userId
      localStorage.setItem('user_id', userId)
    }
  },
  actions: {
    async fetchChallenges({ commit, state }) {
      // First check if we have a valid API key
      if (!state.apiKey) {
        console.error('No API key provided. Challenges cannot be fetched without authentication.')
        return
      }

      try {
        // First validate the API key with the main instance
        const validationResponse = await fetch('/api/validate', {
          method: 'POST',
          headers: {
            'x-api-key': state.apiKey
          }
        })
        
        const validationData = await validationResponse.json()
        
        if (!validationData.valid) {
          console.error('Invalid API key. Cannot fetch challenges.')
          commit('setApiKeyValid', false)
          commit('setSyncEnabled', false)
          return
        }

        // If API key is valid, load challenges from local file
        const response = await fetch('/src/data/challenges.json')
        const challenges = await response.json()
        commit('setChallenges', challenges)
        commit('setApiKeyValid', true)
        commit('setSyncEnabled', true)
        
        // Store the userId if provided
        if (validationData.userId) {
          commit('setUserId', validationData.userId)
        }
      } catch (error) {
        console.error('Error loading challenges:', error)
      }
    },
    async validateApiKey({ commit }, key) {
      try {
        const response = await fetch('/api/validate', {
          method: 'POST',
          headers: {
            'x-api-key': key
          }
        })
        const data = await response.json()
        commit('setApiKeyValid', data.valid)
        commit('setSyncEnabled', data.valid)
        if (data.userId) {
          commit('setUserId', data.userId)
        }
        return data.valid
      } catch (error) {
        console.error('Error validating API key:', error)
        commit('setApiKeyValid', false)
        commit('setSyncEnabled', false)
        return false
      }
    },
    async syncProgress({ state, commit }) {
      if (!state.syncEnabled || !state.apiKey || !state.isApiKeyValid) return

      try {
        const response = await fetch('https://your-main-instance.netlify.app/api/sync', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${state.apiKey}`
          },
          body: JSON.stringify({
            completedDays: state.progress.completedDays,
            completedCommands: state.progress.completedCommands,
            notes: state.progress.notes,
            taskStatus: state.progress.taskStatus
          })
        })
        const data = await response.json()
        if (data.success) {
          if (data.progress) {
            commit('setCompletedDays', data.progress.completedDays)
          }
        }
      } catch (error) {
        console.error('Error syncing progress:', error)
      }
    },
    initializeUser({ commit, dispatch }) {
      commit('setCurrentDay', 1)
      commit('setCompletedDays', [])
      commit('setAllTaskStatus', {})
      commit('setCaptainsLog', {})
      dispatch('fetchChallenges')
    }
  }
})

export default store